import {
  CircleNotch,
  FileText,
  Image as ImageIcon,
} from "@phosphor-icons/react";

import {
  type GetProjectReviewsResponse,
  type ProjectReviewFile,
} from "../../../services/askPunttProjectFeed.types";

/** A small non-interactive file pill shown inside a review bubble. */
function ReviewFileChip({
  file,
  testid,
}: {
  file: ProjectReviewFile;
  testid: string;
}) {
  const isImage = file.type === "image" && file.thumbnailUrl;
  const label = file.name ?? "File";
  return (
    <span
      data-testid={testid}
      className="inline-flex max-w-full items-center gap-1 rounded-md border border-puntt-neutral-gray-5 bg-puntt-neutral-gray-2 px-1.5 py-0.5 text-[11px] text-puntt-neutral-gray-11"
    >
      {isImage ? (
        // eslint-disable-next-line jsx-a11y/alt-text -- decorative; name shown as text
        <img
          src={file.thumbnailUrl ?? undefined}
          alt=""
          aria-hidden="true"
          className="size-3.5 shrink-0 rounded object-cover"
        />
      ) : (
        <FileText size={12} aria-hidden="true" className="shrink-0" />
      )}
      <span className="truncate">{label}</span>
    </span>
  );
}

/**
 * Right-hand REVIEWS panel: an overall status chip (e.g. IN REVIEW) plus a
 * card per active asset under review (name + version badge + thumbnail).
 * Clicking a card deep-links into the asset's review/ticket via `onOpenReview`.
 */
export function ProjectReviewsPanel({
  data,
  isLoading = false,
  onOpenReview,
}: {
  data?: GetProjectReviewsResponse;
  isLoading?: boolean;
  onOpenReview?: (item: GetProjectReviewsResponse["reviews"][number]) => void;
}) {
  const reviews = data?.reviews ?? [];
  const status = data?.status;

  return (
    <section
      className="border-b border-puntt-neutral-gray-5 p-5"
      aria-label="Reviews"
      data-testid="project-reviews-panel"
    >
      <div className="mb-3 flex items-center gap-2">
        <h2 className="text-[11px] font-bold uppercase tracking-wide text-puntt-neutral-gray-11">
          Reviews
        </h2>
        {status && (
          <span
            className="bg-puntt-yellow-3 text-puntt-yellow-11 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
            data-testid="project-reviews-status"
          >
            {status}
          </span>
        )}
      </div>

      {isLoading ? (
        <div
          className="flex items-center justify-center py-4"
          data-testid="project-reviews-loading"
        >
          <CircleNotch
            size={18}
            weight="bold"
            className="animate-spin text-puntt-neutral-gray-9"
          />
        </div>
      ) : reviews.length === 0 ? (
        <p
          className="text-[13px] leading-relaxed text-puntt-neutral-gray-10"
          data-testid="project-reviews-empty"
        >
          No active reviews. Reviews appear here automatically when a new
          version is uploaded.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {reviews.map((item) => {
            // A review card is only actionable once its asset has a backing
            // review ticket. Until then there's no artwork/comments/context to
            // open, so the card is rendered disabled rather than dead-clickable.
            const hasTicket = Boolean(item.ticketId);
            const reviewFiles = item.reviewFiles ?? [];
            const contextAssets = item.contextAssets ?? [];
            const hasChips = reviewFiles.length > 0 || contextAssets.length > 0;
            return (
              <button
                key={item.id}
                type="button"
                disabled={!hasTicket}
                aria-disabled={!hasTicket}
                onClick={() => {
                  if (hasTicket) onOpenReview?.(item);
                }}
                aria-label={`Open review for ${item.name}${
                  item.status ? ` (${item.status})` : ""
                }`}
                data-testid={`project-review-card-${item.id}`}
                className="flex flex-col gap-2 rounded-lg border border-puntt-neutral-gray-5 bg-base-white p-2.5 text-left transition-colors hover:border-puntt-accent-9 hover:bg-puntt-blue-2 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:border-puntt-neutral-gray-5 disabled:hover:bg-base-white"
              >
                <div className="flex items-center gap-3">
                  <span
                    aria-hidden="true"
                    className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-md bg-puntt-neutral-gray-3 text-puntt-neutral-gray-11"
                  >
                    {item.thumbnailUrl ? (
                      // eslint-disable-next-line jsx-a11y/alt-text -- decorative thumbnail; the card carries an aria-label
                      <img
                        src={item.thumbnailUrl}
                        alt=""
                        aria-hidden="true"
                        className="size-full object-cover"
                      />
                    ) : (
                      <ImageIcon size={18} />
                    )}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-puntt-neutral-gray-12">
                    {item.name}
                  </span>
                  {item.versionNumber != null && (
                    <span
                      className="font-mono shrink-0 rounded border border-puntt-neutral-gray-7 bg-base-white px-1.5 py-px text-[10px] font-medium text-puntt-neutral-gray-11"
                      data-testid={`project-review-card-${item.id}-version`}
                    >
                      v{item.versionNumber}
                    </span>
                  )}
                  {item.status && (
                    <span
                      data-testid={`project-review-card-${item.id}-status`}
                      className="shrink-0 rounded-full bg-puntt-neutral-gray-3 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-puntt-neutral-gray-11"
                    >
                      {item.status.replace(/_/g, " ")}
                    </span>
                  )}
                </div>
                {hasChips && (
                  <div className="flex flex-wrap gap-1">
                    {reviewFiles.map((f, idx) => (
                      <ReviewFileChip
                        key={`review-${idx}`}
                        file={f}
                        testid={`project-review-file-${item.id}-${idx}`}
                      />
                    ))}
                    {contextAssets.map((f, idx) => (
                      <ReviewFileChip
                        key={`context-${idx}`}
                        file={f}
                        testid={`project-review-context-${item.id}-${idx}`}
                      />
                    ))}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}
